import { useState, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AUTH_PATHS } from '../constants/auth'
import { type ProductCardGridProps } from '../constants/product'
import { WISHLIST_UI } from '../constants/wishlist'
import { cx, formatCurrency } from '../helpers/format'
import type { CatalogCard } from '../helpers/mapCatalog'
import { useAuth } from '../hooks/useAuth'
import { useWishlist } from '../hooks/useWishlist'
import { Icon } from './Icon'

export function ProductCardGrid({ products }: ProductCardGridProps) {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { isWishlisted, toggle } = useWishlist()
  const [busyId, setBusyId] = useState<string | null>(null)

  if (products.length === 0) return null

  async function onToggleWishlist(
    event: MouseEvent<HTMLButtonElement>,
    product: CatalogCard,
  ) {
    event.preventDefault()
    event.stopPropagation()

    if (!isAuthenticated) {
      navigate(AUTH_PATHS.login, { state: { from: product.to } })
      return
    }

    if (busyId) return
    setBusyId(product.id)
    try {
      await toggle(product.id)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="product-grid">
      {products.map((product) => {
        const wishlisted = isWishlisted(product.id)
        const busy = busyId === product.id

        return (
          <article key={product.id} className="product-card">
            <button
              type="button"
              className={cx('product-card__wishlist', wishlisted && 'is-active')}
              aria-label={wishlisted ? WISHLIST_UI.removeAria : WISHLIST_UI.addAria}
              aria-pressed={wishlisted}
              disabled={busy}
              onClick={(event) => void onToggleWishlist(event, product)}
            >
              <Icon name="heart" size={16} filled={wishlisted} />
            </button>

            <Link to={product.to} className="product-card__link">
              <div className={`product-card__media tone-${product.tone}`}>
                {product.badge ? (
                  <span className="product-card__badge">
                    <span className="product-card__badge-icon" aria-hidden>
                      <Icon name="bolt" size={11} filled />
                    </span>
                    {product.badge}
                  </span>
                ) : null}
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="product-card__image"
                  />
                ) : (
                  <div className="product-card__art" aria-hidden />
                )}
              </div>
              <div className="product-card__body">
                <h3>{product.name}</h3>
                {product.vendor ? <p>{product.vendor}</p> : null}
                <div className="product-card__price">
                  <strong>{formatCurrency(product.price)}</strong>
                  {product.oldPrice != null ? (
                    <span className="product-card__mrp">
                      {formatCurrency(product.oldPrice)}
                    </span>
                  ) : null}
                  {product.discountPercent > 0 ? (
                    <span className="product-card__off">
                      {product.discountPercent}% OFF
                    </span>
                  ) : null}
                </div>
              </div>
            </Link>
          </article>
        )
      })}
    </div>
  )
}
